import _ from "lodash";
import React from "react";
import {useTabsSharedDataContext} from "../components_v2/TabsSharedData/TabsSharedData";
import {getErrorMessage} from "../../../utils/utils";
import {parseResponse} from "../../../utils/api";
import {generateQueryParamsHash, getPortfolioColor} from "../utils";
import {isCancel, useCancelableRequest} from "../../../hooks/useCancelableRequest";

export function useHistoricalData(customerId, portfolios, startDate, endDate, withHistoricalPortfolios, dataProvider, useSharedDataOnly, investmentStrategyId) {
  const [data, setData] = React.useState({
    data: null,
    loading: true,
    errors: null,
    updatedAt: undefined,
  });
  const getCancelToken = useCancelableRequest();

  const sharedData = useTabsSharedDataContext();

  const syncFromShared = () => {
    const sharedPortfolioData = !!sharedData
        ? sharedData.getDataValue('historical-data', generateQueryParamsHash(customerId, portfolios, startDate, endDate, withHistoricalPortfolios))
        : undefined;

      if (!!sharedPortfolioData && !sharedPortfolioData.errors && sharedPortfolioData.updatedAt !== data.updatedAt) {
        // sync internal hook data with shared data.
        setData(sharedPortfolioData);

        return true;
      } else {
        return false;
      }
  };

  React.useEffect(() => {syncFromShared()}, [_.get(sharedData, 'updatedAt')]);

  React.useEffect(() => {
    if (!!customerId && !_.isEmpty(portfolios)) {

      const synced = syncFromShared();
      if(synced) return;

      // If there is not shared data but only shared data should be used - return default data body.
      // useSharedDataOnly will be used in case two charts displayed at the same time. In that case one of them should request data and share it.
      // Another chart should use shared data useSharedDataOnly=true
      if (useSharedDataOnly) {
        setData({
          data: null,
          loading: true,
          errors: null,
          updatedAt: undefined,
        });
        return;
      }

      fetch();
    }
  }, [
    customerId, JSON.stringify(portfolios),
    startDate, endDate, withHistoricalPortfolios]);

  const fetch = async () => {
    const cancelToken = getCancelToken();
    const hashKey = generateQueryParamsHash(customerId, portfolios, startDate, endDate, withHistoricalPortfolios);

    const state = {
      data: null,
      loading: true,
      errors: null,
      updatedAt: +new Date()
    };
    setData(state);
    // It is not possible to sync data with shared data in
    // hook because hook is not executed if component destroyed
    sharedData.setDataValue('historical-data', hashKey, state);

    try {

      const depotNumbers = portfolios.map((portfolio) => portfolio.depotNumber).filter((value) => !!value);

      let response = await dataProvider.getHistoricalData(
        customerId, depotNumbers, startDate, endDate, withHistoricalPortfolios,
        undefined, undefined, investmentStrategyId, false, true, true, true, undefined, cancelToken);

      parseResponse(response, 'historical', (data) => {
        const state = {
          data: {
            portfolios: (data || []).map(p => ({...p, color: getPortfolioColor(portfolios, p)}))
          },
          loading: false,
          errors: undefined,
          updatedAt: +new Date()
        };
        setData(state);
        sharedData.setDataValue('historical-data', hashKey, state);
      }, (errors) => {
        const state = {
          data: null,
          loading: false,
          errors: getErrorMessage(errors),
          updatedAt: +new Date()
        };
        setData(state);
        sharedData.setDataValue('historical-data', hashKey, state);
      });
    }
    catch(errors) {
      const state = {
        data: null,
        loading: false,
        errors: getErrorMessage(errors),
        updatedAt: +new Date()
      };
      if(!isCancel(errors)) setData(state);
      sharedData.setDataValue('historical-data', hashKey, state);
    }
  };

  return data
}